/**
 * Post-login redirect helper
 * Manages where to redirect users after successful login/registration
 * Uses sessionStorage instead of URL query params for SEO benefits
 */

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

/**
 * Save the current page path to sessionStorage for post-login redirect
 */
export const savePostLoginRedirect = (path: string): void => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
    } catch (error) {
      // Silently fail if sessionStorage is not available
      console.warn('Unable to save post-login redirect:', error);
    }
  }
};

/**
 * Get the saved post-login redirect path
 * Falls back to document.referrer if no explicit redirect was saved
 * @param fallback - Default fallback path if nothing is saved
 */
export const getPostLoginRedirect = (fallback?: string): string | null => {
  if (typeof window === 'undefined') {
    return fallback || null;
  }

  try {
    // First, check if there's an explicitly saved redirect
    const savedRedirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (savedRedirect) {
      return savedRedirect;
    }

    // Second, check document.referrer (for middleware redirects)
    if (document.referrer) {
      const referrerUrl = new URL(document.referrer);
      // Only use referrer if it's from the same origin and not the login/register pages
      if (
        referrerUrl.origin === window.location.origin &&
        !referrerUrl.pathname.includes('/account/login') &&
        !referrerUrl.pathname.includes('/account/register') &&
        !referrerUrl.pathname.includes('/account/forgot-password') &&
        !referrerUrl.pathname.includes('/account/reset-password')
      ) {
        return referrerUrl.pathname;
      }
    }

    return fallback || null;
  } catch (error) {
    console.warn('Unable to get post-login redirect:', error);
    return fallback || null;
  }
};

/**
 * Clear the saved post-login redirect
 */
export const clearPostLoginRedirect = (): void => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    } catch (error) {
      // Silently fail
      console.warn('Unable to clear post-login redirect:', error);
    }
  }
};
